/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, CardContent, Grid, Icon, IconButton, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { useTreeItem } from '@mui/x-tree-view/useTreeItem'
import {
    TreeItemContent,
    TreeItemGroupTransition,
    TreeItemIconContainer,
    TreeItemLabel,
    TreeItemRoot,
} from '@mui/x-tree-view/TreeItem'
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon'
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider'
import { useTreeItemModel } from '@mui/x-tree-view/hooks'
import { isUndefined } from 'lodash'
import DescriptionIcon from '@mui/icons-material/Description'

const Action = ({
    onClick = () => {},
    icon,
    color = '#161832',
}) => {
    return (
        <IconButton
            sx={{ fontSize: '24px', padding: '3px' }}
            onClick={e => {
                e.stopPropagation()
                onClick()
            }}
        >
            <Icon fontSize='inherit' sx={{ color }}>{icon}</Icon>
        </IconButton>
    )
}

Action.propTypes = {
    onClick: PropTypes.func,
    icon: PropTypes.string,
    color: PropTypes.string,
}

const CustomLabel = ({ item, actions, children, ...other }) => (
    <TreeItemLabel {...other}>
        <Grid container spacing={1} alignItems='center'>
            <Grid container size='grow' alignItems='center'>
                <Grid size='auto'>
                    <Typography sx={{ fontSize: '1rem' }}>{children}</Typography>
                </Grid>
                {item.description && (
                    <Grid size='auto'>
                        <Tooltip title={item.description}>
                            <DescriptionIcon />
                        </Tooltip>
                    </Grid>
                )}
                <Grid size='grow' />
                {
                    item.nbFeatures && (
                        <Typography variant='h3' sx={{ paddingRight: 20 }}>{item.nbFeatures} fonctionnalités</Typography>
                    )
                }
                {
                    item.stats && (
                        <>
                            <Button variant='contained' onClick={(e) => {
                                e.stopPropagation()
                                item.onClickStat('30J', item.id)
                            }}
                            >30 jours : {item.stats.count_30d}</Button>
                            <Button variant='contained' onClick={(e) => {
                                e.stopPropagation()
                                item.onClickStat('3MO', item.id)
                            }}
                            >3 mois : {item.stats.count_3mo}</Button>
                            <Button variant='contained' onClick={(e) => {
                                e.stopPropagation()
                                item.onClickStat('1Y', item.id)
                            }}
                            >1 an : {item.stats.count_1y}</Button>
                        </>
                    )
                }
                <Grid size='auto'>
                    <Typography sx={{ fontSize: '1rem', width: '150px' }}>{item.price} €</Typography>
                </Grid>
            </Grid>
            {actions.map(({ icon, onClick }, i) => (
                <Grid key={i} size='auto'>
                    <Action
                        icon={icon}
                        onClick={() => onClick(item.id)}
                    />
                </Grid>
            ))}
        </Grid>
    </TreeItemLabel>
)

const CustomTreeItem = React.forwardRef((props, ref) => {
    const {
        id, itemId, label, disabled, children, // for useTreeItem
        actions, // for getLabelProps
        ...other // for getRootProps
    } = props

    const {
        getContextProviderProps,
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        status,
        getGroupTransitionProps,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })
    const item = useTreeItemModel(itemId)
    return (
        <TreeItemProvider {...getContextProviderProps()}>
            <TreeItemRoot {...getRootProps(other)}>
                <TreeItemContent {...getContentProps()} sx={{ borderRadius: '8px', backgroundColor: item.color, color: item.textColor }}>
                    <TreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItemIcon status={status} />
                    </TreeItemIconContainer>
                    <CustomLabel
                        {...getLabelProps({ item, actions })}
                    />
                </TreeItemContent>
                {children && <TreeItemGroupTransition {...getGroupTransitionProps()}
                    sx={{
                        marginLeft: '15px',
                        paddingLeft: '8px',
                        borderLeft: `1px dashed ${alpha('#000000', 0.3)}`,
                    }}
                />}
            </TreeItemRoot>
        </TreeItemProvider>
    )
})

const addDepth = (item, depth = 0) => {
    return {
        ...item,
        depth,
        children: item.subFeatures?.map(it => addDepth(it, depth + 1)),
    }
}

const CommercialFeatureExplorer = ({
    commercialFeature,
    actions = [],
}) => {
    const [expandedItems, setExpandedItems] = useState([])

    const items = useMemo(() => {
        const list = isUndefined(commercialFeature) ? [] : [commercialFeature]
        return list.map(item => addDepth(item, 0))
    }, [commercialFeature])

    const filteredActions = actions.filter(a => a.displayed || isUndefined(a.displayed))

    return (
        <Card>
            <CardContent>
                <RichTreeView
                    items={items}
                    expandedItems={expandedItems}
                    onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
                    slots={{ item: CustomTreeItem }}
                    slotProps={{
                        item: {
                            actions: filteredActions,
                        },
                    }}
                    itemChildrenIndentation={0}
                />
            </CardContent>
        </Card>
    )
}

CommercialFeatureExplorer.propTypes = {
    commercialFeature: PropTypes.shape({}),
    actions: PropTypes.arrayOf(PropTypes.shape({})),
}

export default CommercialFeatureExplorer